import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestsApi, type RequestMode } from '../features/requests/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { canProcure } from '../lib/roles.ts';
import { t } from '../lib/strings.ts';

/**
 * Lets procurement staff pick automatic (RFQ links) vs manual (upload files +
 * pick vendor) quotation collection. Renders nothing unless the viewer is
 * procurement AND the request is still a fresh DRAFT — once vendors are invited
 * (SENT) or the request is awarded (CLOSED), the mode is locked server-side.
 */
export function ModeChooser({ requestId, mode, status }: { requestId: string; mode: RequestMode; status: string }): JSX.Element | null {
  const role = useAuthStore((s) => s.user?.role);
  const qc = useQueryClient();
  const setMode = useMutation({
    mutationFn: (m: RequestMode) => requestsApi.setMode(requestId, m),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['request', requestId] }),
  });

  if (!canProcure(role) || status !== 'DRAFT') return null;

  const target: RequestMode = mode === 'MANUAL' ? 'AUTOMATIC' : 'MANUAL';
  const targetLabel = target === 'MANUAL' ? t.manualQuote.switchToManual : t.manualQuote.switchToAutomatic;
  const currentLabel = mode === 'MANUAL' ? t.manualQuote.modeManual : t.manualQuote.modeAutomatic;
  const currentHint = mode === 'MANUAL' ? t.manualQuote.modeManualHint : t.manualQuote.modeAutomaticHint;

  return (
    <section className="card-paper flex flex-wrap items-center justify-between gap-3 p-4">
      <div className="min-w-0">
        <div className="status-label text-muted-foreground">{t.manualQuote.modeHeading}</div>
        <p className="mt-0.5 text-sm">
          <span className="font-semibold">{currentLabel}</span>
          <span className="text-muted-foreground"> — {currentHint}</span>
        </p>
      </div>
      <button
        type="button"
        onClick={() => setMode.mutate(target)}
        disabled={setMode.isPending}
        className="btn-secondary btn-sm shrink-0"
      >
        {targetLabel}
      </button>
      {setMode.isError && <p className="w-full text-xs text-destructive">{t.manualQuote.switchFailed}</p>}
    </section>
  );
}
